import React, { useState, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeft, CheckCircle, XCircle, Clock, WifiOff, AlertTriangle, TrendingUp, IndianRupee, Eye, RefreshCw, Send, ThumbsUp, Wifi } from 'lucide-react';
import { api } from '../../utils/api';

const StatCard = ({ icon: Icon, label, value, color, subLabel }) => (
  <div className="card">
    <div className="card-body flex items-center gap-4">
      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-xl font-semibold text-gray-900">{value}</p>
        {subLabel && <p className="text-xs text-gray-400 mt-0.5">{subLabel}</p>}
      </div>
    </div>
  </div>
);

const PERIOD_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This Week',
  pastWeek: 'Past Week',
  thisMonth: 'This Month',
};

const getDateRangeForPeriod = (periodKey) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  let startDate, endDate;

  switch (periodKey) {
    case '1day':
    case 'today':
      startDate = new Date(today);
      endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 1);
      break;
    case 'yesterday':
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 1);
      endDate = new Date(today);
      break;
    case '7days':
    case 'thisWeek':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      endDate = new Date(now);
      break;
    case 'pastWeek':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      endDate = new Date(now);
      break;
    case '15days':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 15);
      endDate = new Date(now);
      break;
    case '1month':
    case 'thisMonth':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(now);
      break;
    default:
      return null;
  }

  const toDateStr = (d) => d.toISOString().split('T')[0];
  return { fromDate: toDateStr(startDate), toDate: toDateStr(endDate) };
};

const DriverStatistics = () => {
  const { id } = useParams();
  const location = useLocation();
  const today = new Date().toISOString().split('T')[0];

  const [fromDate, setFromDate] = useState(location.state?.fromDate ?? today);
  const [toDate, setToDate] = useState(location.state?.toDate ?? today);
  const [activePeriod, setActivePeriod] = useState('today');
  const [cancelFilter, setCancelFilter] = useState(location.state?.cancelFilter ?? 'all');
  const [groupTrips, setGroupTrips] = useState(true);

  const handlePeriodChange = (periodKey) => {
    const range = getDateRangeForPeriod(periodKey);
    if (!range) return;
    setActivePeriod(periodKey);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
  };

  const { data, isLoading, error } = useQuery(
    ['driver-stats', id, fromDate, toDate],
    () => api.get(`/drivers/${id}/stats`, { params: { fromDate, toDate } }),
    { enabled: !!id, keepPreviousData: true }
  );

  const { data: driverData } = useQuery(
    ['driver', id],
    () => api.get(`/drivers/${id}`),
    { enabled: !!id }
  );

  const { data: fareConfigData } = useQuery(
    ['fare-config-active'],
    () => api.get('/fare', { params: { isActive: true, limit: 1 } }),
    { staleTime: 5 * 60 * 1000 }
  );

  const fareConfig = fareConfigData?.data?.data?.configs?.[0];
  const stats = data?.data?.data;
  const driver = driverData?.data?.data?.driver || driverData?.data?.data;
  const counts = stats?.counts || {};
  const cancellations = stats?.cancellations || {};
  const earnings = stats?.earnings || {};

  const allCancelledTrips = useMemo(() => {
    const byDriver = (cancellations.byDriver?.trips || []).map(t => ({ ...t, _cancelledBy: 'DRIVER' }));
    const byPassenger = (cancellations.byPassenger?.trips || []).map(t => ({ ...t, _cancelledBy: 'PASSENGER' }));
    return [...byDriver, ...byPassenger];
  }, [cancellations]);

  const filteredCancelledTrips = useMemo(() => {
    if (cancelFilter === 'driver') return allCancelledTrips.filter(t => t._cancelledBy === 'DRIVER');
    if (cancelFilter === 'passenger') return allCancelledTrips.filter(t => t._cancelledBy === 'PASSENGER');
    return allCancelledTrips;
  }, [allCancelledTrips, cancelFilter]);

  const formatCancelReason = (reason) => {
    const map = {
      driver_not_responding: 'Driver not responding',
      driver_cancelled: 'Driver cancelled',
      passenger_cancelled: 'Passenger cancelled',
      driver_not_connected: 'Driver not connected',
      no_driver_found: 'No driver found',
      trip_timeout: 'Trip timed out',
      unknown: 'Unknown reason',
      rider_unreachable: "Rider not reachable or not at pickup location",
    pickup_incorrect_or_far: "Incorrect or far pickup location",
    destination_too_far: "Destination too far or outside service area",
    drop_changed: "Rider changed drop location after booking",
    payment_issue: "Payment mode not acceptable",
    vehicle_issue: "Vehicle issue or low fuel",
    personal_emergency: "Personal emergency or health reason",
    traffic_or_weather: "Poor traffic or weather conditions",
    };
    if (!reason) return null;
    return map[reason.toLowerCase()] || reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getStatusBadge = (trip) => {
    const by = trip.cancelledBy || trip.cancelBy || '';
    const isDriver = by === 'DRIVER';
    const isPassenger = by === 'PASSENGER' || by === 'USER';
    return (
      <span className={`badge ${isDriver ? 'badge-warning' : isPassenger ? 'badge-danger' : 'badge-info'}`}>
        {isDriver ? 'Cancelled by Driver' : isPassenger ? 'Cancelled by Passenger' : 'Cancelled'}
      </span>
    );
  };

  return (
    <div className="space-y-6">

      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center gap-4">
            <Link to="/drivers/stats" className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="page-title">Driver Statistics</h1>
              <p className="page-subtitle">
                {driver?.name ? `Stats for ${driver.name}` : 'Daily trip & earnings breakdown'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Date Filter */}
      <div className="card">
        <div className="card-body space-y-4">
          {/* Date Inputs Row */}
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <label className="form-label">From Date</label>
              <input
                type="date"
                value={fromDate}
                max={toDate || today}
                onChange={(e) => { setFromDate(e.target.value); setActivePeriod(''); }}
                className="input w-48"
              />
            </div>
            <div>
              <label className="form-label">To Date</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={today}
                onChange={(e) => { setToDate(e.target.value); setActivePeriod(''); }}
                className="input w-48"
              />
            </div>
            {(fromDate !== today || toDate !== today) && (
              <button
                onClick={() => { setFromDate(today); setToDate(today); setActivePeriod('today'); }}
                className="text-xs text-blue-600 hover:text-blue-800 underline pb-1"
              >
                Reset to Today
              </button>
            )}
          </div>

          {/* Time Period Buttons Row */}
          <div>
            <label className="form-label">Time Period</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handlePeriodChange(key)}
                  className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                    activePeriod === key
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md ring-1 ring-blue-200'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300'
                  }`}
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setActivePeriod(''); setFromDate(today); setToDate(today); }}
                className="px-3 py-1 text-xs font-medium rounded-md border bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="card">
          <div className="card-body text-center text-red-500">Failed to load statistics.</div>
        </div>
      ) : (
        <>
          {/* Trip Counts */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-700">Trip Counts</h2>
              <button
                type="button"
                onClick={() => setGroupTrips(g => !g)}
                className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
                  groupTrips
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                title={groupTrips ? 'Showing grouped total (same user within 30 min = 1)' : 'Showing raw total'}
              >
                {groupTrips ? 'Grouped' : 'Ungrouped'}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <StatCard icon={TrendingUp}    label="Total Trips"          value={groupTrips ? (counts.totalTrips ?? 0) : (counts.rawTotalTrips ?? counts.totalTrips ?? 0)} color="bg-blue-500" />
              <StatCard icon={Send}           label="Requested"            value={counts.requestedTrips ?? 0}          color="bg-purple-500" />
              <StatCard icon={ThumbsUp}       label="Accepted"             value={counts.acceptedTrips ?? 0}           color="bg-teal-500" />
              <StatCard icon={CheckCircle}   label="Completed"            value={counts.completedTrips ?? 0}          color="bg-green-500" subLabel={(counts.divergedTrips ?? 0) > 0 ? `${(counts.completedTrips ?? 0) - (counts.divergedTrips ?? 0)} completed + ${counts.divergedTrips} diverged` : undefined} />
              <StatCard icon={XCircle}       label="Rejected"             value={counts.rejectedTrips ?? 0}           color="bg-red-500" />
              <StatCard icon={Clock}         label="Timeouts"             value={counts.timeoutTrips ?? 0}            color="bg-yellow-500" />
              <StatCard icon={WifiOff}       label="Socket Not Connected" value={counts.socketNotConnectedTrips ?? 0} color="bg-gray-500" />
              <StatCard icon={AlertTriangle} label="Cancelled"            value={counts.cancelledTrips ?? 0}          color="bg-orange-500" />
            </div>
          </div>

          {/* Earnings */}
          <div>
            <h2 className="text-base font-semibold text-gray-700 mb-3">Earnings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={IndianRupee} label="Earnings for Date" value={`₹${(earnings.driverEarningsForDate ?? 0).toFixed(2)}`} color="bg-green-600" />
              <StatCard icon={IndianRupee} label="Driver Due"        value={`₹${(earnings.driverDue ?? 0).toFixed(2)}`}            color="bg-red-600" />
              <div className="card">
                <div className="card-body">
                  <p className="text-sm text-gray-500">Next Due Date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {earnings.nextDueDate ? new Date(earnings.nextDueDate).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
              <div className="card">
                <div className="card-body flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-purple-500">
                    <RefreshCw className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Due Repay Cycle</p>
                    {fareConfig?.dueRepayCycle ? (
                      <p className="text-xl font-semibold text-gray-900 capitalize">
                        Every {fareConfig.dueRepayCycle.value} {fareConfig.dueRepayCycle.type}(s)
                      </p>
                    ) : (
                      <p className="text-xl font-semibold text-gray-400">—</p>
                    )}
                    {fareConfig && (
                      <p className="text-xs text-gray-400 mt-0.5">{fareConfig.regionCode}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Work History */}
          <div>
            <h2 className="text-base font-semibold text-gray-700 mb-3">Work History</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Wifi className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Total Online Hours</p>
                  <p className="text-xl font-semibold text-green-800">{stats?.workHistory?.totalOnlineFormatted ?? '0h 0m'}</p>
                </div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                  <WifiOff className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-red-700">Total Offline Hours</p>
                  <p className="text-xl font-semibold text-red-800">{stats?.workHistory?.totalOfflineFormatted ?? '0h 0m'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cancelled Trips */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-700">
                Cancelled Trips
              </h2>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>By Driver: {cancellations.byDriver?.count ?? 0}</span>
                <span>By Passenger: {cancellations.byPassenger?.count ?? 0}</span>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900">
                    Cancelled Trips ({filteredCancelledTrips.length})
                  </h3>
                  <select
                    value={cancelFilter}
                    onChange={(e) => setCancelFilter(e.target.value)}
                    className="input w-56"
                  >
                    <option value="all">All Cancellations</option>
                    <option value="driver">Cancellation by Driver</option>
                    <option value="passenger">Cancellation by Passenger</option>
                  </select>
                </div>
              </div>
              <div className="card-body">
                {filteredCancelledTrips.length === 0 ? (
                  <p className="text-sm text-gray-400">No cancellations found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Trip ID</th>
                          <th className="table-header-cell">Passenger Name</th>
                          <th className="table-header-cell">Driver Name</th>
                          <th className="table-header-cell">Route</th>
                          <th className="table-header-cell">Status</th>
                          <th className="table-header-cell">Action</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {filteredCancelledTrips.map((trip, idx) => {
                          const formatLocation = (loc) => {
                            if (!loc) return null;
                            if (loc.address) return loc.address;
                            if (Array.isArray(loc.coordinates))
                              return `${loc.coordinates[1]?.toFixed(4)}, ${loc.coordinates[0]?.toFixed(4)}`;
                            if (loc.latitude != null)
                              return `${Number(loc.latitude).toFixed(4)}, ${Number(loc.longitude).toFixed(4)}`;
                            if (Array.isArray(loc))
                              return `${loc[1]?.toFixed(4)}, ${loc[0]?.toFixed(4)}`;
                            return null;
                          };
                          const startLabel = formatLocation(trip.startLocation);
                          const endLabel   = formatLocation(trip.endLocation);
                          return (
                          <tr key={trip.rideId || idx} className="table-row">
                            <td className="table-cell">
                              <div className="text-sm font-medium text-gray-900">
                                {trip.rideId || trip.tripId || ''}
                              </div>
                              <div className="text-xs text-gray-400">
                                {trip.bookingTime ? new Date(trip.bookingTime).toLocaleString() : ''}
                              </div>
                            </td>
                            <td className="table-cell">
                              <div className="text-sm text-gray-900">{trip.bookingForName || '—'}</div>
                            </td>
                            <td className="table-cell">
                              <div className="text-sm text-gray-900">{driver?.name || '—'}</div>
                            </td>
                            <td className="table-cell">
                              <div className="space-y-1 text-xs text-gray-600">
                                {startLabel ? (
                                  <div className="flex items-start gap-1">
                                    <span className="mt-0.5 h-2 w-2 bg-green-500 rounded-full inline-block flex-shrink-0"></span>
                                    <span>{startLabel}</span>
                                  </div>
                                ) : null}
                                {endLabel ? (
                                  <div className="flex items-start gap-1">
                                    <span className="mt-0.5 h-2 w-2 bg-red-500 rounded-full inline-block flex-shrink-0"></span>
                                    <span>{endLabel}</span>
                                  </div>
                                ) : null}
                                {!startLabel && !endLabel && <span className="text-gray-400">—</span>}
                              </div>
                            </td>
                            <td className="table-cell">
                              <span className={`badge ${trip._cancelledBy === 'DRIVER' ? 'badge-warning' : 'badge-danger'}`}>
                                {trip._cancelledBy === 'DRIVER' ? 'By Driver' : 'By Passenger'}
                              </span>
                              {trip.reason && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {formatCancelReason(trip.reason)}
                                </div>
                              )}
                            </td>
                            <td className="table-cell">
                              <Link
                                to={`/trips/${trip.tripId}`}
                                state={{ from: location.pathname, fromDate, toDate, cancelFilter }}
                                className="text-primary-600 hover:text-primary-900"
                                title="View Trip Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Link>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DriverStatistics;
